import { getErrorMessage } from '../../utils/errorMessage';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { KeyRound, Loader2, Mail, MessageCircle, Phone, ShieldCheck, X } from 'lucide-react';
import api from '../../utils/api';
import WechatQrFlow from '../auth/WechatQrFlow';
import {
  createWechatReauthSession,
  pollWechatReauthSession,
  sendEmailReauthCode,
  verifyEmailReauthCode,
} from '../auth/wechatAuthApi';

const METHOD_META = {
  password: { icon: KeyRound, label: '登录密码', labelEn: 'Password' },
  phone: { icon: Phone, label: '短信验证码', labelEn: 'SMS code' },
  email: { icon: Mail, label: '邮件验证码', labelEn: 'Email code' },
  wechat: { icon: MessageCircle, label: '微信扫码', labelEn: 'WeChat QR' },
};

function getError(error, fallback) {
  return getErrorMessage(error, fallback);
}

export default function SessionReauthDialog({
  loginMethods,
  onClose,
  onVerified,
  english = false,
  darkActive = false,
}) {
  const [method, setMethod] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [countdown, setCountdown] = useState(0);
  const [destination, setDestination] = useState('');
  const [sending, setSending] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState({ type: '', message: '' });

  const methods = useMemo(() => {
    const result = [];
    if (loginMethods?.email?.bound) result.push('password', 'email');
    if (loginMethods?.phone?.bound) result.push('phone');
    if (loginMethods?.wechat?.bound) result.push('wechat');
    return result;
  }, [loginMethods]);

  useEffect(() => {
    if (countdown <= 0) return undefined;
    const timer = window.setTimeout(() => setCountdown(value => Math.max(0, value - 1)), 1000);
    return () => window.clearTimeout(timer);
  }, [countdown]);

  useEffect(() => {
    const onKeyDown = event => event.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  const finish = useCallback(async result => {
    setFeedback({ type: 'success', message: english ? 'Identity verified for two hours.' : '身份验证成功，2 小时内无需重复验证' });
    await onVerified(result?.reauthenticated_until);
  }, [english, onVerified]);

  const verifyPassword = async event => {
    event.preventDefault();
    if (!password || submitting) return;
    setSubmitting(true);
    setFeedback({ type: '', message: '' });
    try {
      const response = await api.post('/auth/reauth/password', { password });
      await finish(response.data);
    } catch (error) {
      setFeedback({ type: 'error', message: getErrorMessage(getError(error, '身份验证失败')) });
    } finally {
      setSubmitting(false);
    }
  };

  const sendCode = async () => {
    if (sending || countdown > 0) return;
    setSending(true);
    setFeedback({ type: '', message: '' });
    try {
      const result = method === 'phone'
        ? (await api.post('/auth/reauth/phone/code')).data
        : await sendEmailReauthCode();
      setDestination(result.phone || result.email || '');
      setCountdown(Number(result.resend_after_seconds) || 60);
      if (result.dev_code) setCode(result.dev_code);
      setFeedback({
        type: 'success',
        message: result.dev_code
          ? (english ? 'Development code filled automatically.' : `开发模式：验证码 ${result.dev_code} 已自动填入`)
          : (english ? 'Verification code sent.' : '验证码已发送'),
      });
    } catch (error) {
      setFeedback({ type: 'error', message: getErrorMessage(getError(error, '验证码发送失败')) });
    } finally {
      setSending(false);
    }
  };

  const verifyCode = async event => {
    event.preventDefault();
    if (!/^\d{6}$/.test(code) || submitting) return;
    setSubmitting(true);
    setFeedback({ type: '', message: '' });
    try {
      const result = method === 'phone'
        ? (await api.post('/auth/reauth/phone/verify', { code })).data
        : await verifyEmailReauthCode(code);
      await finish(result);
    } catch (error) {
      setFeedback({ type: 'error', message: getErrorMessage(getError(error, '身份验证失败')) });
    } finally {
      setSubmitting(false);
    }
  };

  const wechatAuthorized = useCallback(async result => {
    await finish(result);
  }, [finish]);

  const chooseMethod = nextMethod => {
    setMethod(nextMethod);
    setPassword('');
    setCode('');
    setCountdown(0);
    setDestination('');
    setFeedback({ type: '', message: '' });
  };

  let body;
  if (!method) {
    body = (
      <div className="account-reauth-methods">
        <p>{english ? 'Choose any sign-in method already bound to this account.' : '请选择当前账号已绑定的任一方式验证身份。'}</p>
        {methods.map(item => {
          const meta = METHOD_META[item];
          const Icon = meta.icon;
          return (
            <button key={item} type="button" onClick={() => chooseMethod(item)}>
              <Icon size={17} />
              <span>{english ? meta.labelEn : meta.label}</span>
            </button>
          );
        })}
      </div>
    );
  } else if (method === 'password') {
    body = (
      <form className="account-binding-form" onSubmit={verifyPassword}>
        <label>
          <span>{english ? 'Login password' : '登录密码'}</span>
          <div className="account-binding-field">
            <input type="password" autoComplete="current-password" maxLength={64} value={password} onChange={event => setPassword(event.target.value)} autoFocus />
          </div>
        </label>
        <button className="account-binding-submit" type="submit" disabled={!password || submitting}>
          {submitting && <Loader2 className="account-spin" size={14} />}
          {submitting ? (english ? 'Verifying…' : '验证中…') : (english ? 'Verify' : '验证身份')}
        </button>
      </form>
    );
  } else if (method === 'wechat') {
    body = (
      <div className="account-binding-dialog__wechat">
        <WechatQrFlow
          createSession={createWechatReauthSession}
          pollSession={pollWechatReauthSession}
          onAuthorized={wechatAuthorized}
          purpose="reauth"
          darkActive={darkActive}
          english={english}
        />
        <p>{english ? 'The scanned WeChat identity must match this account.' : '扫码微信必须与当前账号绑定的微信一致。'}</p>
      </div>
    );
  } else {
    body = (
      <form className="account-binding-form" onSubmit={verifyCode}>
        {destination ? <div className="account-binding-email-target"><ShieldCheck size={15} /><span>{destination}</span></div> : null}
        <label>
          <span>{method === 'phone' ? (english ? 'SMS code' : '短信验证码') : (english ? 'Email code' : '邮件验证码')}</span>
          <div className="account-binding-field">
            <input inputMode="numeric" autoComplete="one-time-code" value={code} onChange={event => setCode(event.target.value.replace(/\D/g, '').slice(0, 6))} placeholder={english ? 'Six-digit code' : '六位验证码'} />
            <button type="button" onClick={sendCode} disabled={sending || countdown > 0}>
              {sending ? (english ? 'Sending…' : '发送中…') : countdown > 0 ? `${countdown}s` : (english ? 'Send code' : '获取验证码')}
            </button>
          </div>
        </label>
        <button className="account-binding-submit" type="submit" disabled={!/^\d{6}$/.test(code) || submitting}>
          {submitting && <Loader2 className="account-spin" size={14} />}
          {submitting ? (english ? 'Verifying…' : '验证中…') : (english ? 'Verify' : '验证身份')}
        </button>
      </form>
    );
  }

  return createPortal(
    <div className="account-binding-backdrop" role="presentation" onMouseDown={event => event.target === event.currentTarget && onClose()}>
      <section className="account-binding-dialog" role="dialog" aria-modal="true" aria-labelledby="session-reauth-title">
        <header>
          <span className="account-binding-dialog__icon"><ShieldCheck size={18} /></span>
          <div><span className="account-eyebrow">SECURITY CHECK</span><h2 id="session-reauth-title">{english ? 'Verify your identity' : '验证身份'}</h2></div>
          <button type="button" onClick={onClose} aria-label={english ? 'Close' : '关闭'}><X size={16} /></button>
        </header>
        <div className="account-binding-dialog__body">
          {method ? <button className="account-binding-back" type="button" onClick={() => chooseMethod('')}>{english ? 'Choose another method' : '选择其他验证方式'}</button> : null}
          {feedback.message ? <div className={`account-binding-feedback is-${feedback.type}`} role={feedback.type === 'error' ? 'alert' : 'status'}><span>{feedback.message}</span></div> : null}
          {body}
          <p className="account-reauth-validity">{english ? 'Verification remains valid for two hours on this signed-in device.' : '验证结果仅对当前登录设备生效，有效期 2 小时。'}</p>
        </div>
      </section>
    </div>,
    document.body,
  );
}
